// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/MessageReactions.tsx
// 🔧 PURPOSE: Reaction picker + chips for guest chat messages.
//             Backed by POST /api/public/chat/messages/:id/reactions on
//             the channel manager. Server enforces a 6-emoji whitelist
//             and one reaction per side (guest|admin) per message with
//             toggle/replace semantics.
// ============================================

import React, { useState } from 'react';
import { SmilePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { EMOJI_REACTIONS, MessageReaction } from '../lib/guestApi';

interface MessageReactionsProps {
  reactions: MessageReaction[] | undefined;
  isOwnMessage: boolean;
  onReact: (emoji: string) => void;
  /**
   * Il tasto "aggiungi reazione" (faccina col +). La chat lo mette solo
   * sotto l'ultimo messaggio dello staff: sotto ogni bolla era rumore.
   * Le reazioni gia' date si vedono comunque.
   */
  canAdd?: boolean;
}

const MessageReactions: React.FC<MessageReactionsProps> = ({
  reactions,
  isOwnMessage,
  onReact,
  canAdd = true,
}) => {
  const { t } = useTranslation();
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const ownReaction = reactions?.find((r) => r.senderType === 'guest');
  const adminReaction = reactions?.find((r) => r.senderType === 'admin');

  // Niente da mostrare e niente da aggiungere: nessuna riga vuota sotto la bolla.
  if (!ownReaction && !adminReaction && !canAdd) return null;

  const handleSelect = (emoji: string) => {
    setIsPickerOpen(false);
    onReact(emoji);
  };

  // Ogni tocco e' da 44px, ma i margini negativi fanno "sporgere" l'area
  // sopra e sotto: tra un messaggio e l'altro non cresce spazio.
  return (
    <div
      className={`-my-1.5 flex items-center ${
        isOwnMessage ? '-mr-1.5 justify-end' : '-ml-1.5 justify-start'
      }`}
    >
      {ownReaction && (
        <button
          type="button"
          onClick={() => onReact(ownReaction.emoji)}
          className="flex min-h-[44px] min-w-[44px] items-center justify-center px-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded-full"
          aria-label={t('guestChat.reactions.removeYours', { emoji: ownReaction.emoji })}
          title={t('guestChat.reactions.youReactedWith', { emoji: ownReaction.emoji })}
        >
          <span className="inline-flex h-7 items-center rounded-full bg-blue-100 px-2.5 text-[15px] leading-none dark:bg-blue-900/50">
            {ownReaction.emoji}
          </span>
        </button>
      )}

      {adminReaction && (
        <span
          className="mx-1 inline-flex h-7 items-center rounded-full bg-stone-200/80 px-2.5 text-[15px] leading-none dark:bg-stone-700/80"
          title={t('guestChat.reactions.reactedWith', {
            name: adminReaction.senderName || 'Nonna Vittoria',
            emoji: adminReaction.emoji,
          })}
        >
          {adminReaction.emoji}
        </span>
      )}

      {canAdd && (
        <Popover open={isPickerOpen} onOpenChange={setIsPickerOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex h-11 w-11 items-center justify-center rounded-full text-stone-600 transition-colors hover:bg-stone-200/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:text-stone-400 dark:hover:bg-stone-800"
              aria-label={t('guestChat.reactions.add')}
            >
              <SmilePlus className="h-[19px] w-[19px]" strokeWidth={2} aria-hidden="true" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align={isOwnMessage ? 'end' : 'start'}
            sideOffset={2}
            className="w-auto rounded-full border-nva-edge bg-nva-card p-0.5 px-1 shadow-[0_10px_26px_rgba(42,38,32,.16)] dark:border-stone-700 dark:bg-stone-900"
          >
            <div className="flex items-center">
              {EMOJI_REACTIONS.map((emoji) => {
                const isActive = ownReaction?.emoji === emoji;
                return (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => handleSelect(emoji)}
                    className={`flex h-11 w-11 items-center justify-center rounded-full text-[22px] leading-none transition-transform motion-safe:hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${
                      isActive ? 'bg-blue-100 dark:bg-blue-900/40' : ''
                    }`}
                    aria-label={emoji}
                    aria-pressed={isActive}
                  >
                    {emoji}
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export default MessageReactions;
